import React from 'react';
import { Building2, Car, Edit3, Home, Leaf, ShieldCheck, Snowflake, Thermometer, Zap } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { evaluateShowWhen } from './showWhen';

const questionVisibleForReview = (question, answers) => evaluateShowWhen(question.showWhen, answers);

const Row = ({ label, value }) => (
  <div className='flex items-start justify-between gap-3 py-1.5'>
    <span className='text-xs text-muted-foreground shrink-0'>{label}</span>
    <span className='text-sm text-right font-medium min-w-0 break-words'>{value}</span>
  </div>
);

const optionLabel = (question, val) => {
  if (val == null || val === '') return '—';
  const opts = question.options || [];
  const hit = opts.find((o) => String(o.value) === String(val));
  return hit?.label != null ? String(hit.label) : String(val);
};

const formatScalarAnswer = (question, value) => {
  if (value == null || value === '') return '—';
  if (question.type === 'boolean' || question.type === 'single_choice' || question.type === 'select') {
    return optionLabel(question, value);
  }
  if (question.type === 'weekly_toggle') {
    return Array.isArray(value) && value.length ? value.join(', ') : '—';
  }
  return String(value);
};

const formatRepeaterLegs = (question, rows) => {
  if (!Array.isArray(rows) || !rows.length) return [];
  const fields = question.fields || [];
  return rows.map((row, legIndex) => {
    const parts = [];
    fields.forEach((field) => {
      if (!evaluateShowWhen(field.showWhen, row)) return;
      const piece = formatScalarAnswer(field, row[field.key]);
      if (piece && piece !== '—') parts.push(piece);
    });
    return {
      label: `Leg ${legIndex + 1}`,
      value: parts.length ? parts.join(' · ') : '—',
    };
  });
};

const iconForSlug = (slug) => {
  switch (slug) {
    case 'employment':
      return Building2;
    case 'working-pattern':
      return Home;
    case 'wfh-equipment':
      return Home;
    case 'lighting':
      return Zap;
    case 'heating':
      return Thermometer;
    case 'cooling':
      return Snowflake;
    case 'renewable':
      return Leaf;
    case 'commuting':
      return Car;
    default:
      return Home;
  }
};

const Section = ({ title, icon: Icon, children, onEditSlug, editSlug }) => (
  <div className='rounded-xl border border-border p-3.5'>
    <div className='flex items-center justify-between mb-2'>
      <div className='flex items-center gap-2 min-w-0'>
        <div className='w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center text-accent shrink-0'>
          <Icon size={14} strokeWidth={1.5} />
        </div>
        <div className='font-display text-sm truncate'>{title}</div>
      </div>
      <Button type='button' variant='ghost' size='sm' className='shrink-0 h-8 px-2 text-xs text-accent' onClick={() => onEditSlug(editSlug)}>
        <Edit3 size={11} strokeWidth={1.5} className='mr-1' />
        Edit
      </Button>
    </div>
    <div>{children}</div>
  </div>
);

/**
 * Read-only summary before submit (prototype ReviewSubmit).
 * `steps` should be the visible step list (excluding `review` when iterating).
 */
const SurveyReviewStep = ({ steps = [], questions = [], answers = {}, onEditSlug, sectionTitleForSlug }) => {
  const slugsInOrder = steps.map((s) => s.slug).filter((slug) => slug && slug !== 'review');

  return (
    <div className='space-y-4'>
      {slugsInOrder.map((slug) => {
        const qs = questions
          .filter((q) => q.stepSlug === slug && questionVisibleForReview(q, answers))
          .sort((a, b) => {
            const ao = Number.isFinite(a.questionOrder) ? a.questionOrder : 0;
            const bo = Number.isFinite(b.questionOrder) ? b.questionOrder : 0;
            return ao - bo;
          });
        if (!qs.length) return null;

        const SectionIcon = iconForSlug(slug);
        const title = sectionTitleForSlug?.(slug) || slug;

        return (
          <Section key={slug} title={title} icon={SectionIcon} editSlug={slug} onEditSlug={onEditSlug}>
            {qs.flatMap((question) => {
              const raw = answers[question.key];
              if (question.type === 'repeater') {
                const legs = formatRepeaterLegs(question, Array.isArray(raw) ? raw : []);
                if (!legs.length) return [<Row key={question.key} label={question.question} value='—' />];
                return legs.map((leg, idx) => (
                  <Row key={`${question.key}-leg-${idx}`} label={leg.label} value={leg.value} />
                ));
              }
              return [<Row key={question.key} label={question.question} value={formatScalarAnswer(question, raw)} />];
            })}
          </Section>
        );
      })}

      <div className='flex items-center gap-2 flex-wrap'>
        <Badge variant='outline' size='default'>
          Emissions estimate is calculated after you submit.
        </Badge>
      </div>

      <Alert>
        <ShieldCheck size={14} strokeWidth={1.5} />
        <AlertTitle>Ready to submit</AlertTitle>
        <AlertDescription>
          Once you tap Submit we&apos;ll add your answers to the aggregate totals. You can still amend within 24 hours via
          your original email link.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default SurveyReviewStep;
